import { Pressable, View } from "react-native";
import StyledText from "../../../common/ui/styled-text";
import SettingsItem from "../common/settings-item";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import { Checkbox } from "../../../common/shadcn/checkbox";
import { Label } from "../../../common/shadcn/label";
import { emptySchedule, ISchedule } from "../../../../types/agent-settings";
import HoursSelector from "./hours-selector";
import { useState, useEffect } from "react";
import { validateOrderingHours } from "../../../../utils/setting-helpers";
import { useIsTemporarilyClosed } from "../../../../hooks/useIsTemporarilyClosed";

const OrderingHours = () => {
  const { settings, handleSaveOrderingHours, validationErrors } =
    useSettingsData();
  const isStreamPOS = settings.posProvider === "stream";
  const isTempClosed = useIsTemporarilyClosed();

  const [isEditing, setIsEditing] = useState(false);

  // save what the user had set before turning on the checkbox, so they can toggle without losing their hours
  const [savedCustomHours, setSavedCustomHours] = useState<ISchedule | null>(
    settings.orderingHours
  );

  useEffect(() => {
    if (settings.orderingHours !== null) {
      setSavedCustomHours(settings.orderingHours);
    }
  }, [settings.orderingHours]);

  const handleCheckboxChange = (checked: boolean) => {
    if (isStreamPOS) return;

    if (checked) {
      handleSaveOrderingHours(null);
    } else {
      handleSaveOrderingHours(savedCustomHours || emptySchedule);
    }
  };

  const editButton = isStreamPOS ? null : (
    <Pressable
      className={`px-4 py-2 rounded-md flex-shrink-0 bg-gray-100 hover:bg-gray-200 border border-gray-200`}
      disabled={isTempClosed}
      onPress={() => {
        setIsEditing(!isEditing);
      }}
    >
      <StyledText className={`text-sm text-gray-900`}>
        {isEditing ? "Done" : "Edit"}
      </StyledText>
    </Pressable>
  );

  return (
    <View>
      {!isStreamPOS && (
        <View
          className={`flex flex-row items-center gap-3 mb-1 ${
            !!settings.orderingHours && "pb-6"
          }`}
        >
          <Checkbox
            id="match-restaurant-hours"
            checked={settings.orderingHours == null}
            onCheckedChange={(checked) =>
              handleCheckboxChange(Boolean(checked))
            }
            disabled={isStreamPOS || isTempClosed}
          />
          <Label htmlFor="match-restaurant-hours">
            Kitchen hours match store hours
          </Label>
        </View>
      )}
      {settings.orderingHours && (
        <SettingsItem
          title="Kitchen Hours"
          description="Set the times when your kitchen is open to accept Loman orders (may differ from your store hours)."
          leftItem={editButton}
          disabled={isTempClosed}
        >
          <View>
            {/* stream warning */}
            {isStreamPOS && (
              <>
                <View className="bg-blue-50 rounded p-2 mb-3">
                  <StyledText className="text-xs text-blue-800">
                    To change these hours, update them directly in your POS
                    system.
                  </StyledText>
                </View>
                {!validateOrderingHours(
                  settings.orderingHours,
                  settings.schedule
                ) && (
                  <View className="bg-yellow-50 rounded p-2 mb-3">
                    <StyledText className="text-xs text-yellow-800">
                      Some kitchen hours fall outside your store's open hours.
                      Ordering is disabled during these times.
                    </StyledText>
                  </View>
                )}
              </>
            )}

            {settings.orderingHours && (
              <HoursSelector
                allowedScheduleBounds={settings.schedule}
                schedule={savedCustomHours || emptySchedule}
                isEditing={isEditing}
                onUpdateSchedule={handleSaveOrderingHours}
                disabled={isStreamPOS}
                readonly={
                  isTempClosed || (!!settings.orderingHours && isStreamPOS)
                }
                validationError={validationErrors.orderingHours}
                closedText="Not Accepting Orders"
              />
            )}
          </View>
        </SettingsItem>
      )}
    </View>
  );
};

export default OrderingHours;
